import React, { useState } from 'react';

interface GestioneStruttureProps {
  onBack: () => void;
}

interface Struttura {
  id: number;
  nome: string;
  via: string;
  email: string;
}

type AlertType = {
  message: string;
  type: 'error' | 'success';
} | null;

const GestioneStrutture: React.FC<GestioneStruttureProps> = ({ onBack }) => {
  const [strutture, setStrutture] = useState<Struttura[]>([]);
  const [nome, setNome] = useState('');
  const [via, setVia] = useState('');
  const [email, setEmail] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [alert, setAlert] = useState<AlertType>(null);

  const showAlert = (message: string, type: 'error' | 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const campiCompilati = nome && via && email;

  const aggiungiStruttura = () => {
    if (!campiCompilati) {
      showAlert('Compila tutti i campi', 'error');
      return;
    }

    const nuovaStruttura: Struttura = {
      id: Date.now(),
      nome,
      via,
      email
    };

    setStrutture([...strutture, nuovaStruttura]);
    showAlert('Struttura aggiunta con successo!', 'success');
    resetForm();
  };

  const resetForm = () => {
    setNome('');
    setVia('');
    setEmail('');
  };

  const avviaModifica = (id: number) => {
    setEditingId(id);
  };

  const salvaModifica = (id: number) => {
    setEditingId(null);
    showAlert('Struttura modificata con successo!', 'success');
  };

  const annullaModifica = () => {
    setEditingId(null);
  };

  const aggiornaStruttura = (id: number, campo: keyof Struttura, valore: any) => {
    setStrutture(strutture.map(str => 
      str.id === id ? { ...str, [campo]: valore } : str
    ));
  };

  const rimuoviStruttura = (id: number) => {
    setStrutture(strutture.filter(str => str.id !== id));
    showAlert('Struttura eliminata', 'success');
    if (editingId === id) {
      setEditingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-5">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-8 text-white flex justify-between items-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div>
              <h1 className="text-3xl font-bold mb-2">🏢 Gestione Strutture</h1>
              <p className="opacity-90">Inserisci e modifica le strutture</p>
            </div>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all"
            >
              ← Indietro
            </button>
          </div>

          <div className="p-8">
            {alert && (
              <div className={`p-3 rounded-lg mb-5 flex items-center gap-2 ${alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                <span>{alert.type === 'error' ? '❌' : '✅'}</span>
                <span>{alert.message}</span>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📋 Nuova Struttura</h2>
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-purple-500">
                <div className="mb-4">
                  <label className="block mb-2 font-medium text-gray-600">🏢 Nome Struttura</label>
                  <input
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Es: Sede Centrale Milano"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2 font-medium text-gray-600">📍 Indirizzo</label>
                  <input
                    type="text"
                    value={via}
                    onChange={(e) => setVia(e.target.value)}
                    placeholder="Es: Via Roma 123, Milano"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <div className="mb-4">
                  <label className="block mb-2 font-medium text-gray-600">📧 Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="struttura@email.com"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={aggiungiStruttura}
                  disabled={!campiCompilati}
                  className={`w-full p-3 rounded-lg text-white font-medium transition-all ${
                    campiCompilati 
                      ? 'bg-green-500 hover:bg-green-600 cursor-pointer' 
                      : 'bg-gray-400 cursor-not-allowed opacity-60'
                  }`}
                >
                  + Aggiungi Struttura
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 pb-2 border-b-2">📊 Lista Strutture ({strutture.length})</h2>
              {strutture.length === 0 ? (
                <p className="text-center text-gray-400 py-8">Nessuna struttura ancora inserita</p>
              ) : (
                <div className="space-y-3">
                  {strutture.map(str => (
                    <div key={str.id} className={`bg-gray-50 p-5 rounded-lg border-l-4 transition-all ${
                      editingId === str.id ? 'border-purple-400 bg-purple-50' : 'border-purple-500'
                    }`}>
                      {editingId === str.id ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">🏢 Nome</label>
                            <input
                              type="text"
                              value={str.nome}
                              onChange={(e) => aggiornaStruttura(str.id, 'nome', e.target.value)}
                              className="w-full p-2 border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">📍 Indirizzo</label>
                            <input
                              type="text"
                              value={str.via}
                              onChange={(e) => aggiornaStruttura(str.id, 'via', e.target.value)}
                              className="w-full p-2 border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-600">📧 Email</label>
                            <input
                              type="email"
                              value={str.email}
                              onChange={(e) => aggiornaStruttura(str.id, 'email', e.target.value)}
                              className="w-full p-2 border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none"
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => annullaModifica()}
                              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all text-sm"
                            >
                              ✕ Annulla
                            </button>
                            <button
                              onClick={() => salvaModifica(str.id)}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-sm"
                            >
                              ✓ Salva
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex-1 min-w-[200px]">
                            <div className="font-bold text-lg">{str.nome}</div>
                            <div className="text-sm text-gray-600">📍 {str.via}</div>
                            <div className="text-sm text-gray-600">📧 {str.email}</div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => avviaModifica(str.id)}
                              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => rimuoviStruttura(str.id)}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all text-sm"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestioneStrutture;